import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Edit, 
  Trash2,
  Briefcase,
  Calendar,
  FileText,
  Receipt,
  Download
} from "lucide-react";
import { API_BASE } from "../lib/api";

const API_URL = API_BASE;

const statusConfig = {
  brouillon: { label: "Brouillon", className: "bg-zinc-800 text-zinc-300 border-zinc-700" },
  en_cours: { label: "En cours", className: "bg-blue-900/50 text-blue-300 border-blue-800" },
  termine: { label: "Terminé", className: "bg-emerald-900/50 text-emerald-300 border-emerald-800" },
  annule: { label: "Annulé", className: "bg-red-900/50 text-red-300 border-red-800" },
};

const formatCurrency = (amount, devise = "FCFA") => {
  if (devise === "EUR") {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount);
  }
  return new Intl.NumberFormat("fr-FR").format(amount) + " FCFA";
};

const emptyMission = {
  client_id: "",
  titre: "",
  description: "",
  duree_mois: 1,
  date_debut: new Date().toISOString().split("T")[0],
  montant_net: "",
  devise: "FCFA",
  numero_bon_commande: "",
  numero_devis: "",
  numero_proforma: "",
  numero_facture: "",
  statut: "brouillon",
};

export default function Missions() {
  const [missions, setMissions] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [formData, setFormData] = useState(emptyMission);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [missionsRes, clientsRes] = await Promise.all([
        apiClient.get("/missions"),
        apiClient.get("/clients"),
      ]);
      setMissions(missionsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        montant_net: parseFloat(formData.montant_net),
        duree_mois: parseInt(formData.duree_mois),
      };
      
      if (editingMission) {
        await apiClient.put(`/missions/${editingMission.id}`, payload);
        toast.success("Mission mise à jour");
      } else {
        await apiClient.post("/missions", payload);
        toast.success("Mission créée avec paiements générés (30% acompte + échéances mensuelles)");
      }
      setDialogOpen(false);
      setEditingMission(null);
      setFormData(emptyMission);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (mission) => {
    setEditingMission(mission);
    setFormData({
      client_id: mission.client_id,
      titre: mission.titre,
      description: mission.description || "",
      duree_mois: mission.duree_mois,
      date_debut: mission.date_debut,
      montant_net: (mission.montant_net || mission.montant_total || mission.montant_ttc || mission.montant_ht || 0).toString(),
      devise: mission.devise,
      numero_bon_commande: mission.numero_bon_commande || "",
      numero_devis: mission.numero_devis || "",
      numero_proforma: mission.numero_proforma || "",
      numero_facture: mission.numero_facture || "",
      statut: mission.statut,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette mission ?")) return;
    try {
      await apiClient.delete(`/missions/${id}`);
      toast.success("Mission supprimée");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDownloadPDF = (missionId, docType) => {
    const token = localStorage.getItem("token");
    const url = `${API_URL}/missions/${missionId}/pdf/${docType}`;
    
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(response => {
        if (!response.ok) throw new Error("Erreur de téléchargement");
        return response.blob();
      })
      .then(blob => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${docType}_${missionId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success(`${docType.charAt(0).toUpperCase() + docType.slice(1)} téléchargé`);
      })
      .catch(() => toast.error("Erreur lors du téléchargement"));
  };

  const filteredMissions = missions.filter((mission) => {
    const matchesSearch = mission.titre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || mission.statut === filterStatus;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid="missions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Missions</h1>
          <p className="text-zinc-500 mt-1">Gérez vos missions et projets</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingMission(null);
            setFormData(emptyMission);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 glow-primary" data-testid="add-mission-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle mission
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121214] border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-white">
                {editingMission ? "Modifier la mission" : "Nouvelle mission"}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                {editingMission ? "Modifiez les détails de la mission" : "Créez une nouvelle mission avec paiements automatiques"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">Client *</Label>
                <Select
                  value={formData.client_id}
                  onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                  required
                >
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="mission-client-select">
                    <SelectValue placeholder="Sélectionner un client" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.nom} - {client.entreprise}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Titre de la mission *</Label>
                <Input
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  required
                  data-testid="mission-titre-input"
                  className="bg-zinc-900/50 border-zinc-800"
                  placeholder="Audit SI, Développement web, etc."
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="mission-description-input"
                  className="bg-zinc-900/50 border-zinc-800 min-h-[80px]"
                  placeholder="Description détaillée de la mission..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Durée (mois) *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.duree_mois}
                    onChange={(e) => setFormData({ ...formData, duree_mois: e.target.value })}
                    required
                    data-testid="mission-duree-input"
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Date de début *</Label>
                  <Input
                    type="date"
                    value={formData.date_debut}
                    onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                    required
                    data-testid="mission-date-input"
                    className="bg-zinc-900/50 border-zinc-800"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Montant NET (TPS incluse) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.montant_net}
                    onChange={(e) => setFormData({ ...formData, montant_net: e.target.value })}
                    required
                    data-testid="mission-montant-input"
                    className="bg-zinc-900/50 border-zinc-800"
                    placeholder="5100000"
                  />
                  <p className="text-xs text-zinc-500">Montant reçu (TPS payée par le client)</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Devise</Label>
                  <Select
                    value={formData.devise}
                    onValueChange={(value) => setFormData({ ...formData, devise: value })}
                  >
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="mission-devise-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121214] border-zinc-800">
                      <SelectItem value="FCFA">FCFA</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Documents */}
              <div className="border-t border-zinc-800 pt-4 mt-4">
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Documents</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">N° Bon de commande</Label>
                    <Input
                      value={formData.numero_bon_commande}
                      onChange={(e) => setFormData({ ...formData, numero_bon_commande: e.target.value })}
                      data-testid="mission-bc-input"
                      className="bg-zinc-900/50 border-zinc-800 font-mono"
                      placeholder="BC-2024-001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">N° Devis</Label>
                    <Input
                      value={formData.numero_devis}
                      onChange={(e) => setFormData({ ...formData, numero_devis: e.target.value })}
                      data-testid="mission-devis-input"
                      className="bg-zinc-900/50 border-zinc-800 font-mono"
                      placeholder="DEV-2024-001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">N° Proforma</Label>
                    <Input
                      value={formData.numero_proforma}
                      onChange={(e) => setFormData({ ...formData, numero_proforma: e.target.value })}
                      data-testid="mission-proforma-input"
                      className="bg-zinc-900/50 border-zinc-800 font-mono"
                      placeholder="PRO-2024-001"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">N° Facture</Label>
                    <Input
                      value={formData.numero_facture}
                      onChange={(e) => setFormData({ ...formData, numero_facture: e.target.value })}
                      data-testid="mission-facture-input"
                      className="bg-zinc-900/50 border-zinc-800 font-mono"
                      placeholder="FAC-2024-001"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Statut</Label>
                <Select
                  value={formData.statut}
                  onValueChange={(value) => setFormData({ ...formData, statut: value })}
                >
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-800" data-testid="mission-statut-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {!editingMission && (
                <div className="p-3 rounded-lg bg-blue-900/20 border border-blue-900/30 text-sm text-blue-300">
                  <p>Les paiements seront automatiquement générés :</p>
                  <ul className="list-disc list-inside mt-1 text-blue-300/80">
                    <li>30% d'acompte au lancement</li>
                    <li>70% en mensualités égales sur la durée de la mission</li>
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-zinc-700 text-zinc-300"
                >
                  Annuler
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="mission-submit-btn">
                  {editingMission ? "Mettre à jour" : "Créer"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Rechercher une mission..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-800"
            data-testid="missions-search-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] bg-zinc-900/50 border-zinc-800" data-testid="missions-filter-select">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-zinc-800">
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(statusConfig).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Missions List */}
      <div className="grid gap-4">
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Chargement...</div>
        ) : filteredMissions.length === 0 ? (
          <Card className="bg-[#121214] border-zinc-800">
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
              <p className="text-zinc-500">Aucune mission trouvée</p>
              <p className="text-zinc-600 text-sm mt-1">Créez votre première mission pour commencer</p>
            </CardContent>
          </Card>
        ) : (
          filteredMissions.map((mission) => (
            <Card key={mission.id} className="bg-[#121214] border-zinc-800 card-hover" data-testid={`mission-card-${mission.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading font-semibold text-white truncate">{mission.titre}</h3>
                      <Badge variant="outline" className={statusConfig[mission.statut]?.className}>
                        {statusConfig[mission.statut]?.label}
                      </Badge>
                    </div>
                    {mission.client && (
                      <p className="text-sm text-zinc-400 mb-3">
                        {mission.client.nom} - {mission.client.entreprise}
                      </p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Montant NET</p>
                        <p className="font-mono text-white">{formatCurrency(mission.montant_net || mission.montant_ttc || mission.montant_total || mission.montant_ht || 0, mission.devise)}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Durée</p>
                        <p className="text-zinc-300">{mission.duree_mois} mois</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Début</p>
                        <p className="font-mono text-zinc-300">{mission.date_debut}</p>
                      </div>
                      <div>
                        <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Fin</p>
                        <p className="font-mono text-zinc-300">{mission.date_fin}</p>
                      </div>
                    </div>
                    {(mission.numero_bon_commande || mission.numero_facture) && (
                      <div className="flex gap-3 mt-3">
                        {mission.numero_bon_commande && (
                          <Badge variant="outline" className="border-zinc-700 text-zinc-400 font-mono text-xs">
                            <FileText className="w-3 h-3 mr-1" />
                            BC: {mission.numero_bon_commande}
                          </Badge>
                        )}
                        {mission.numero_facture && (
                          <Badge variant="outline" className="border-zinc-700 text-zinc-400 font-mono text-xs">
                            <Receipt className="w-3 h-3 mr-1" />
                            FAC: {mission.numero_facture}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white" data-testid={`mission-menu-${mission.id}`}>
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#121214] border-zinc-800">
                      <DropdownMenuItem onClick={() => handleEdit(mission)} className="text-zinc-300 focus:bg-zinc-800">
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <DropdownMenuItem onClick={() => handleDownloadPDF(mission.id, "devis")} className="text-blue-400 focus:bg-zinc-800">
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger Devis
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDownloadPDF(mission.id, "proforma")} className="text-blue-400 focus:bg-zinc-800">
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger Proforma
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDownloadPDF(mission.id, "facture")} className="text-emerald-400 focus:bg-zinc-800">
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger Facture
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <DropdownMenuItem 
                        onClick={() => handleDelete(mission.id)} 
                        className="text-red-400 focus:bg-zinc-800"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
